import React, { type FC, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import IconButton from '@mui/material/IconButton';
import { Pencil, Trash2 } from 'lucide-react';
import Button from '@mui/material/Button';
import cloneDeep from 'lodash/cloneDeep';
import Typography from '@mui/material/Typography';

import { Flex, FlexColumn } from 'src/components/flex';
import SearchInput from 'src/pages/landlord/conversations/components/header/ConversationsSearchInput';
import FilterStatus from 'src/pages/landlord/conversations/components/header/ConversationsFilterStatus';
import FilterButton from 'src/pages/landlord/conversations/components/header/ConversationsFilterButton';
import { useCreateInbox } from 'src/services/api/inboxes/use-create-inbox';
import { useUpdateInbox } from 'src/services/api/inboxes/use-update-inbox';
import { useDeleteInbox } from 'src/services/api/inboxes/use-delete-inbox';
import { InboxCreateEditForm, type SubmitPayload } from 'src/pages/landlord/inboxes/InboxCreateEditForm';
import ConfirmationDialog from 'src/components/confirmation-dialog';
import { usePrimaryInbox } from 'src/services/api/inboxes/use-primary-inbox';
import type { TConversationsFilter } from 'src/services/api/messages/use-conversations-search';
import useCurrentInbox, { TEAM_INBOX_ID } from 'src/services/api/inboxes/use-current-inbox';
import { getInboxUrl } from 'src/services/api/inboxes/use-all-inboxes';
import type { IInbox } from 'src/services/api/inboxes/types';
import { INITIAL_FILTERS } from 'src/pages/landlord/conversations/ConversationsInboxPage';
import { useProperties, useLoggedInUser } from 'src/services/api';
import { FilteringBy } from 'src/pages/landlord/conversations/components/header/ConversationsFilterModal';
import GroupBySelector, { type GroupByOption, GroupByOption as GroupByOptions } from 'src/pages/landlord/conversations/components/header/GroupBySelector';
import { useFlags } from '@flagsmith/flagsmith/react';
import { EFeatureFlag } from 'src/utils/feature-flags';

type ConversationsHeaderProps = {
    searchScope: 'inbox' | 'all';
    filter: TConversationsFilter;
    setFilter: (newFilter: Partial<TConversationsFilter>) => void;
    resetFilter: () => void;
    search: string;
    setSearch: (newSearch: string) => void;
    isFilterOpen: boolean;
    setFilterOpen: (open: boolean) => void;
    inbox?: IInbox;
    groupBy?: GroupByOption;
    setGroupBy?: (value: GroupByOption) => void;
}

type InboxEditedItem = { id?: number, name?: string, is_private?: boolean, filters?: TConversationsFilter };

type ConversationsInboxHeaderProps = {
    filter: TConversationsFilter;
    setFilter: (newFilter: Partial<TConversationsFilter>) => void;
    inbox?: IInbox;
}

const ConversationsInboxHeader = ({
    filter: searchFilter,
    setFilter,
    inbox,
}: ConversationsInboxHeaderProps) => {
    const { data: loggedInUser } = useLoggedInUser();
    const { goToInboxesList } = usePrimaryInbox();
    const navigate = useNavigate();

    const [isCreateOrUpdateInboxModalOpen, setIsCreateOrUpdateInboxModalOpen] = useState(false);
    const [editedItem, setEditedItem] = useState<InboxEditedItem | undefined>(undefined);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const { mutateAsync: createInbox, isLoading: createLoading } = useCreateInbox();
    const { mutateAsync: updateInbox, isLoading: updateLoading } = useUpdateInbox(inbox?.id);
    const { mutateAsync: deleteInbox, isLoading: deleteLoading } = useDeleteInbox();

    const isDefaultInbox = inbox?.id === TEAM_INBOX_ID;
    const isOwner = !inbox?.is_virtual && loggedInUser?.id === inbox?.landlord_user_id;

    const isFilterEdited = isDefaultInbox ? !isEqual(searchFilter, INITIAL_FILTERS) : !isEqual(searchFilter, inbox?.filters);
    const isFiltersEmpty = inbox ? isEqual(searchFilter, INITIAL_FILTERS) : false;
    const isSubmitting = createLoading || updateLoading || deleteLoading;

    const submitCreateOrUpdate = async (values: SubmitPayload) => {
        setIsCreateOrUpdateInboxModalOpen(false);
        setEditedItem(undefined);
        if (editedItem?.id) {
            await updateInbox(values);
            setFilter(values.filters);
        } else {
            const { inbox: createdInbox } = await createInbox(values);
            // Navigate to the newly created inbox so the edited filters now match a
            // saved view, which clears the dirty state and hides "Save as New Inbox".
            navigate(getInboxUrl(createdInbox));
        }
    };

    const handleCloseModal = () => {
        setIsCreateOrUpdateInboxModalOpen(false);
        setEditedItem(undefined);
    };

    const handleDuplicateInbox = () => {
        setEditedItem({ filters: cloneDeep(searchFilter) });
        setIsCreateOrUpdateInboxModalOpen(true);
    };

    const handleDeleteInbox = async () => {
        if (isDefaultInbox || !inbox) { return; }
        setIsDeleteConfirmOpen(false);
        deleteInbox(inbox.id);
        goToInboxesList();
    };

    const handleOpenEditModal = () => {
        if (!inbox) { return; }
        setEditedItem({
            name: inbox.name,
            is_private: inbox.is_private,
            filters: cloneDeep(inbox.filters),
            id: inbox.id,
        });
        setIsCreateOrUpdateInboxModalOpen(true);
    };

    return (
        <>
            <Flex justifyContent="space-between" mobileFlexDirection="column">
                <Flex alignItems="center" gap={2}>
                    <Typography variant="h2" fontSize={24} fontWeight={600} lineHeight={1} py={1} className="inbox-title">
                        {inbox?.name ?? 'Team Inbox'}
                    </Typography>
                </Flex>
                <Flex alignItems="center" gap={1}>
                    {isFilterEdited && !isFiltersEmpty && !inbox?.is_virtual &&
                        <Button variant="outlined" onClick={handleDuplicateInbox}>Save as New Inbox</Button>}
                    {isOwner && (<>
                        <IconButton onClick={handleOpenEditModal} sx={{ border: '1px solid #10b981', borderRadius: '20%', padding: '6px' }}>
                            <Pencil size="20" color="#10b981" />
                        </IconButton>
                        <IconButton onClick={() => setIsDeleteConfirmOpen(true)} sx={{ border: '1px solid red', borderRadius: '20%', padding: '6px' }}>
                            <Trash2 color="red" size="20" />
                        </IconButton>
                    </>)}
                </Flex>
            </Flex>
            {isCreateOrUpdateInboxModalOpen && (
                <InboxCreateEditForm
                    onSubmit={submitCreateOrUpdate}
                    handleClose={handleCloseModal}
                    initialValues={editedItem}
                    isLoading={isSubmitting}
                />
            )}
            {isDeleteConfirmOpen && (
                <ConfirmationDialog
                    title={`Are you sure you want to delete "${inbox?.name}" ?`}
                    content="This action cannot be undone."
                    onConfirm={handleDeleteInbox}
                    onClose={() => setIsDeleteConfirmOpen(false)}
                    confirmButtonColor="error"
                    confirmButtonText="Delete"
                />
            )}
        </>
    );
};

const SearchHeader = () => {
    return (
        <Typography variant="h2" fontSize={24} fontWeight={600} lineHeight={1} py={1}>
            Search Conversations
        </Typography>
    );
};

const ConversationsHeader: FC<ConversationsHeaderProps> = ({
    searchScope,
    filter,
    setFilter,
    resetFilter,
    search,
    setSearch,
    isFilterOpen,
    setFilterOpen,
    inbox: inboxProp,
    groupBy,
    setGroupBy,
}) => {
    const { pathname } = useLocation();
    const isInboxesPage = pathname.startsWith('/landlord/inner/inboxes');

    const { inbox: currentInbox } = useCurrentInbox();
    const inbox = inboxProp ?? currentInbox;

    // Fetch data using queries
    const { data: properties } = useProperties();
    const { data: loggedInUser } = useLoggedInUser();
    const flags = useFlags([EFeatureFlag.INBOX_GROUP_BY_PROPERTY]);

    const groupByOptions = useMemo(() => {
        const options = [GroupByOptions.FRESHNESS];
        if (flags.inbox_group_by_property?.enabled) {
            options.push(GroupByOptions.PROPERTY);
        }
        return options;
    }, [flags.inbox_group_by_property?.enabled]);

    // Compute team members from logged in user
    const teamMembers = useMemo(() => {
        if (!loggedInUser?.leasing_team_members) {
            return [];
        }
        return loggedInUser.leasing_team_members;
    }, [loggedInUser]);

    return (
        <>
            <FlexColumn p={2}>
                {isInboxesPage ? (
                    <ConversationsInboxHeader
                        filter={filter}
                        setFilter={setFilter}
                        inbox={inbox}
                    />
                ) : <SearchHeader />}
                <Flex alignItems="center" gap={2}>
                    <SearchInput
                        searchScope={searchScope}
                        search={search}
                        setSearch={setSearch}
                    />
                    <FilterButton
                        isFilterOpen={isFilterOpen}
                        setFilterOpen={setFilterOpen}
                        filter={filter}
                        properties={properties ?? []}
                        setFilter={setFilter}
                        resetFilter={resetFilter}
                        disabledFilters={isInboxesPage ? [FilteringBy.LeadIsArchived] : []}
                    />
                    {groupBy !== undefined && setGroupBy && (
                        <GroupBySelector value={groupBy} onChange={setGroupBy} enabledOptions={groupByOptions} />
                    )}
                </Flex>

                <Flex
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{
                        '& .MuiChip-root': { maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' },
                        '& .MuiSvgIcon-root': { ml: 1, fontSize: 20 }
                    }}
                >
                    <FilterStatus
                        teamMembers={teamMembers}
                        filter={filter}
                        setFilter={setFilter}
                    />
                </Flex>
            </FlexColumn>
        </>
    );
};

export default ConversationsHeader;
